import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { HugeiconsIcon } from '@hugeicons/react';
import { Alert01Icon } from '@hugeicons/core-free-icons';

interface FigmaEmbedProps {
  fileKey: string;
  nodeId?: string;
  className?: string;
}

/**
 * Figma's own embed iframe. Deliberately TOKENLESS: Figma authorizes the
 * viewer against their own Figma login and file access, so this renders for a
 * teammate who has never connected Figma to OverlayQA.
 *
 * It used to accept a page list fetched with the VIEWER's OverlayQA-held Figma
 * token, which is a per-user fact the project's file link is not — a teammate
 * without one collected a 400 NOT_CONNECTED per mount (incident 2026-09-01).
 * The page list only ever overrode `nodeId`, and the ticket already carries the
 * node it was captured against, so the fetch bought nothing the ticket does not
 * already know.
 */
export function FigmaEmbed({ fileKey, nodeId, className }: FigmaEmbedProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const embedUrl = useMemo(() => {
    let figmaUrl = `https://www.figma.com/design/${fileKey}`;
    if (nodeId) {
      figmaUrl += `?node-id=${nodeId.replace(/:/g, '-')}&mode=design`;
    }
    return `https://www.figma.com/embed?embed_host=overlayqa&url=${encodeURIComponent(figmaUrl)}`;
  }, [fileKey, nodeId]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError) {
    return (
      <div
        className={clsx(
          'flex flex-col items-center justify-center bg-gray-100 rounded-lg p-8',
          className
        )}
      >
        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mb-4">
          <HugeiconsIcon icon={Alert01Icon} size={20} strokeWidth={2} color="currentColor" className="w-5 h-5 text-gray-500" aria-hidden="true" />
        </div>
        <p className="text-gray-600 text-center mb-4">
          Failed to load Figma embed
        </p>
        <a
          href={`https://www.figma.com/design/${fileKey}${nodeId ? `?node-id=${nodeId.replace(/:/g, '-')}&mode=design` : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-500 hover:underline"
        >
          <span className="text-cap-trim block">Open in Figma</span>
        </a>
      </div>
    );
  }

  return (
    <div className={clsx('relative overflow-hidden', className)}>
      {/* Loading state */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg z-10">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-2" aria-hidden="true"></div>
            <span className="text-sm text-gray-500">Loading Figma...</span>
          </div>
        </div>
      )}

      {/* Iframe */}
      <iframe
        title="Figma design embed"
        src={embedUrl}
        className={clsx(
          'w-full h-full border-0 rounded-lg',
          isLoading && 'opacity-0'
        )}
        allowFullScreen
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
